import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import bemClass from '@upsales/components/Utils/bemClass';
import { Icon, OutsideClick, Text } from '@upsales/components';
import './ClientCardOverflowMenu.scss';

export type OverflowTab = {
	id: string;
	iconHtml: string;
	label: string;
	counter: string;
};

type Props = {
	items: OverflowTab[];
	onSelect: (id: string) => void;
};

type Position = { top: number; left: number };

const DROPDOWN_WIDTH = 240;

const ClientCardOverflowMenu = ({ items, onSelect }: Props) => {
	const classes = new bemClass('ClientCardOverflowMenu');
	const [isOpen, setIsOpen] = useState(false);
	const [didOpenTime, setDidOpenTime] = useState<number | null>(null);
	const [position, setPosition] = useState<Position | null>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement & { wrapperRef: HTMLDivElement }>(null);

	useEffect(() => {
		setDidOpenTime(isOpen ? Date.now() : null);
	}, [isOpen]);

	// Anchor the portal-rendered dropdown to the trigger using viewport coords.
	// We render it via createPortal so it escapes the client-card nav's
	// overflow:hidden safety net.
	useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current) {
			return;
		}
		const rect = triggerRef.current.getBoundingClientRect();
		setPosition({
			top: rect.bottom,
			left: rect.left
		});
	}, [isOpen]);

	// Nothing to collapse — don't render the trigger at all. ng-show can't be
	// used on the host <li> because the card-nav <li> has display:inline-block
	// !important from the sub-page-navigation mixin, which wins the specificity
	// fight against .ng-hide. Early return must come after all hooks.
	if (items.length === 0) {
		return null;
	}

	const handleSelect = (id: string) => {
		setIsOpen(false);
		onSelect(id);
	};

	return (
		<div className={classes.b()}>
			<div
				ref={triggerRef}
				className={classes.elem('trigger').mod({ open: isOpen }).b()}
				onMouseDown={() => setIsOpen(v => !v)}
			>
				<Icon name="bars" />
			</div>
			{isOpen && position
				? createPortal(
						<OutsideClick
							className={classes.elem('dropdown').b()}
							style={{
								position: 'fixed',
								top: position.top,
								left: position.left,
								width: DROPDOWN_WIDTH
							}}
							targetRef={() => [dropdownRef.current?.wrapperRef ?? null, triggerRef.current]}
							outsideClick={() => {
								if (didOpenTime !== null && Date.now() - didOpenTime > 10) {
									setIsOpen(false);
								}
							}}
							listen={isOpen}
							ref={dropdownRef}
						>
							{items.map(item => (
								<div
									key={item.id}
									className={classes.elem('item').b()}
									onMouseDown={() => handleSelect(item.id)}
								>
									<span
										className={classes.elem('icon').b()}
										// eslint-disable-next-line react/no-danger
										dangerouslySetInnerHTML={{ __html: item.iconHtml }}
									/>
									<Text className={classes.elem('label').b()}>{item.label}</Text>
									{item.counter ? (
										<Text size="sm" color="grey-11">
											{item.counter}
										</Text>
									) : null}
								</div>
							))}
						</OutsideClick>,
						document.body
				  )
				: null}
		</div>
	);
};

export default ClientCardOverflowMenu;
